import { IOptionProps } from "@blueprintjs/core";
import { getIn } from "formik";
import {
  ButtonsBloc,
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  AffixRequestApi,
  ERequestState,
  KennelNameChangeRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../../api";
import {
  CancelButton,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IKennelNameChangeRequestDetailProps {
  kennelNameChangeRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const KennelNameChangeRequestDetail: React.FunctionComponent<IKennelNameChangeRequestDetailProps> = ({
  kennelNameChangeRequestId,
  editMode,
  requestInfo,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const personApi = useApiService(PersonApi);

  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [kennelTypes, ktloading] = useReferential((a) => a.getKennelTypes(), false, []);

  const [kennelId, setKennelId] = React.useState();

  // =========================== DATA MANIPULATION ==================================================

  const { data, loading, saving, saveItem, refresh } = useCrudApi<KennelNameChangeRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = kennelNameChangeRequestId) =>
          id > 0
            ? api.getKennelNameChangeRequest(id)
            : new KennelNameChangeRequestDto({
                request: new RequestDto({
                  status: ERequestState.REQUESTED,
                  remark: "",
                  requestDate: new Date(),
                }),
              }).toJSON(),
        saveApiFn: (d: KennelNameChangeRequestDto) => api.saveKennelNameChangeRequest(d),
        onSavedRoute: (d) => `${ERoutes.affixRequest}/${d.kennelNameChangeRequestId}/detail/edit/kennelNameChange`,
      }),
      [api, kennelNameChangeRequestId]
    ),
    [kennelNameChangeRequestId]
  );

  const [openedKennels, okloading] = useReferential((a) => !loading && a.getOpenedKennels(data?.kennelId), true, [
    loading,
  ]);

  const generateNameOfferList = React.useCallback((formik) => {
    const names = [];
    for (let index = 1; index < 6; index++) {
      const element = getIn(formik?.values, `nameOffer${index}`);
      if (element) {
        names.push(element);
      }
    }
    return names?.map((p) => {
      return { value: p, label: p };
    }) as IOptionProps[];
  }, []);

  // =========================== EVENT MANAGER ==================================================
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  // =========================== FORM ==================================================
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      nameOffer1: Yup.string().nullable().required(t(ETLCodes.Required)),
      nameOffer1Type: Yup.string().nullable().required(t(ETLCodes.Required)),
      finalName: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), function (value) {
          if (data?.request?.status === ERequestState.TO_EVALUATE_FCI) {
            return !!value;
          }
          return true;
        }),
      fciNumber: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), function (value) {
          if (data?.request?.status === ERequestState.TO_EVALUATE_FCI) {
            return !!value;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-kennel-owner", t(ETLCodes.IsNotKennelOwner), async function (value) {
            if (kennelId && (kennelNameChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED)) {
              const result = await api.isPersonOwnerOfKennelQuery(value, kennelId);
              return result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (kennelNameChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("is-begian", t(ETLCodes.IsNotBelgian), async function (value) {
            if (kennelNameChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isPersonBelgian(value);
              return result.value;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data?.request?.status, kennelId, kennelNameChangeRequestId, personApi, t]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (kennelNameChangeRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [kennelNameChangeRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      return (
        data?.request?.status === ERequestState.REQUESTED ||
        data?.request?.status === ERequestState.AWAITING_APPLICANT_REACTION ||
        data?.request?.status === ERequestState.BILLED ||
        data?.request?.status === ERequestState.PAID
      );
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.affixRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="kennelId" onChanged={(value) => setKennelId(value)} />
        <FieldSet
          title={t(ETLCodes.KennelNameChangeRequest)}
          rightElement={
            kennelNameChangeRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )
          }
        >
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="request.requestDate"
                label={t(ETLCodes.RequestDate)}
                requiredMark
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGSelectInput
                name="kennelId"
                label={t(ETLCodes.Kennel)}
                items={openedKennels}
                loading={okloading}
                onItemViewClick={(item) => openNewTab(`${ERoutes.kennel}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
              />
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
                showWarning={true}
              />
            </div>
            <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
          </FieldGroup>
        </FieldSet>

        <FieldSet title={t(ETLCodes.FileAffix)}>
          <FieldGroup columns={2}>
            <FGTextInput
              name="nameOffer1"
              label={t(ETLCodes.NameOffer1)}
              trim={true}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer1Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
          </FieldGroup>
        </FieldSet>
        {(data?.request?.status === ERequestState.TO_EVALUATE_FCI ||
          data?.request?.status === ERequestState.EXECUTED) && (
          <FieldSet title={t(ETLCodes.FCIDecision)}>
            <FGCustomPanel>
              {(ctx) => {
                return (
                  <FGSelectInput
                    name="finalName"
                    label={t(ETLCodes.FinalName)}
                    items={generateNameOfferList(ctx.formik)}
                    loading={ktloading}
                    readonly={
                      data?.request?.status !== ERequestState.AWAITING_APPLICANT_REACTION &&
                      (!statusEditable || data?.request?.isBlocked)
                    }
                  />
                );
              }}
            </FGCustomPanel>
            <FGTextInput
              name="fciNumber"
              label={t(ETLCodes.FciNumber)}
              readonly={
                ![ERequestState.AWAITING_APPLICANT_REACTION, ERequestState.TO_EVALUATE_FCI].includes(
                  data?.request.status
                ) &&
                (!statusEditable || data?.request?.isBlocked)
              }
            />
          </FieldSet>
        )}
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.affixRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
